'use client';

import { memo, useEffect, useState } from 'react';
import Image from 'next/image';
import classNames from 'classnames';

import usePrefersReducedMotion from '@/hooks/usePrefersReducedMotion';
import { CAROUSEL_INTERVAL_MS } from '@/constants/carousel';
import useStyles from './styles';
import { TEST_IDS } from './constants/testIds';

export type CarouselMedia = {
  /** The still. Also the `poster` when `videoUrl` is set. */
  url: string;
  /** Set to play the slide instead of showing its still. */
  videoUrl?: string;
  width?: number;
  height?: number;
};

type MediaCarouselProps = {
  /** Every preview, uncapped — a carousel has no reason to hide the tail. */
  images: CarouselMedia[];
  title?: string;
  className?: string;
  /**
   * Applied to each slide's media element, mirroring `Media`'s prop of the same name. Callers use
   * it for things that belong to the media rather than its frame — the grid's hover zoom, and the
   * preset hero's `objectFit: contain`. Dropping it made a multi-output hero crop where a
   * single-output one letterboxed.
   */
  mediaClassName?: string;
  sizes?: string;
  priority?: boolean;
  /** Applied to the frame; omit where the parent already sizes the box. */
  aspectRatio?: string;
  /**
   * Makes the frame a button that reports which slide was showing when it was
   * clicked. Used by the profile grid, where a run card has to open the *result
   * on screen* — the index lives in here, so only this component can say which
   * that is.
   *
   * Omitted everywhere else: a preset preview isn't pickable, and a button that
   * ignores clicks lies to a screen reader.
   */
  onSlideClick?: (index: number) => void;
  /**
   * Skips Next's image optimizer.
   *
   * Required for anything that isn't CMS media: generation results live on
   * `gcdn.picsart.com/magic-flow/…`, which is not in `next.config.mjs`
   * `remotePatterns`, so `next/image` throws `Invalid src prop` on sight. They
   * also already carry the Picsart CDN's own `type=webp&to=min&r=` transform,
   * so optimising them again would re-encode an image that is already the right
   * size — the same reason every other tile in the profile grid passes this.
   */
  unoptimized?: boolean;
  dataTestId?: string;
};

const DEFAULT_SIZES = '(max-width: 640px) 90vw, (max-width: 1024px) 45vw, 22rem';

/**
 * A multi-output preset's previews, one at a time, advancing on their own.
 *
 * The alternative — subdividing one frame — makes each output too small to read on a tile. A
 * preview is not pickable the way a finished result is, so the space is better spent showing each
 * output at full size in turn.
 *
 * Non-interactive by design: no arrows, no swipe. The hosts are a preset tile and a hero that
 * already own the click (opening the preset) and the horizontal drag (the page's scroll), so a
 * control here would compete with both. The dots are indicators, not buttons.
 */
const MediaCarousel = ({
  images,
  title,
  className,
  mediaClassName,
  sizes = DEFAULT_SIZES,
  priority = false,
  aspectRatio,
  onSlideClick,
  unoptimized = false,
  dataTestId = TEST_IDS.ROOT,
}: MediaCarouselProps) => {
  const classes = useStyles();
  const prefersReducedMotion = usePrefersReducedMotion();
  const [index, setIndex] = useState(0);

  const total = images.length;
  const isAnimated = total > 1 && !prefersReducedMotion;

  useEffect(() => {
    if (!isAnimated) return undefined;

    const timer = window.setInterval(() => setIndex(current => (current + 1) % total), CAROUSEL_INTERVAL_MS);
    return () => window.clearInterval(timer);
  }, [isAnimated, total]);

  // A preset whose preview count changed under us must not keep pointing past the end.
  useEffect(() => {
    setIndex(current => (current < total ? current : 0));
  }, [total]);

  // A button only when it acts.
  const Frame = onSlideClick ? 'button' : 'div';

  return (
    <Frame
      className={classNames(classes.frame, className, { [classes.interactive]: onSlideClick })}
      style={aspectRatio ? { aspectRatio } : undefined}
      data-testid={dataTestId}
      {...(onSlideClick ? { type: 'button' as const, onClick: () => onSlideClick(index), 'aria-label': `${title || 'Result'} ${index + 1} of ${total}` } : {})}
    >
      <div className={classNames(classes.track, { [classes.trackStatic]: prefersReducedMotion })} style={{ transform: `translateX(-${index * 100}%)` }}>
        {images.map((image, slide) => (
          <div key={image.url} className={classes.slide}>
            {image.videoUrl && slide === index ? (
              <video
                className={classNames(classes.media, mediaClassName)}
                poster={image.url}
                src={image.videoUrl}
                autoPlay
                muted
                loop
                playsInline
                preload="auto"
                controls={false}
                disableRemotePlayback
                disablePictureInPicture
                aria-label={`${title || 'Preset preview'} ${slide + 1} of ${total}`}
              />
            ) : (
              <Image
                src={image.url}
                alt={`${title || 'Preset preview'} ${slide + 1} of ${total}`}
                fill
                sizes={sizes}
                priority={priority && slide === 0}
                fetchPriority={priority && slide === 0 ? 'high' : 'auto'}
                unoptimized={unoptimized}
                className={classNames(classes.media, mediaClassName)}
              />
            )}
          </div>
        ))}
      </div>
    </Frame>
  );
};

export default memo(MediaCarousel);
